import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import { getDatabase, ref, get, set } from "firebase/database";

const firebaseConfig = {
  apiKey: "AIzaSyBQejsC9Uug7gYYcoFnjIR_fsx_2GyLDww",
  authDomain: "relation-stamp.firebaseapp.com",
  databaseURL: "https://relation-stamp-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "relation-stamp",
  storageBucket: "relation-stamp.firebasestorage.app",
  messagingSenderId: "229190086852",
  appId: "1:229190086852:web:470f790e018f97aed15991"
};

const firebaseApp = initializeApp(firebaseConfig);
const db = getDatabase(firebaseApp);

const GROQ_MODEL = "llama-3.3-70b-versatile";

const KNOWLEDGE_NL = `Je gebruikt de volgende kennis over hoe mensen hun genderidentiteit beschrijven:
- Non-binaire mensen beschrijven hun gender vaak als "buiten de grenzen vallen", "nooit precies passen", "zowel als geen van beiden tegelijk"
- Genderfluide mensen beschrijven hun identiteit als "veranderend met de situatie", "golfbewegingen", "soms meer het een, soms meer het ander"
- Trans mensen beschrijven vaak een gevoel van "altijd al geweten", "een innerlijk weten dat botste met wat anderen zagen", "eindelijk mezelf zien in de spiegel"
- Agender mensen beschrijven gender als "iets dat niet op hen van toepassing is", "neutraal", "gewoon mezelf zonder label"
- Cisgender mensen beschrijven hun gender vaak als iets wat ze nooit hebben hoeven nadenken, "gewoon vanzelfsprekend"
- Genderqueer mensen beschrijven hun identiteit als "opzettelijk buiten de normen", "spelen met verwachtingen", "beide en geen van beide"

Kennisbank van genderidentiteiten:
Agender, Androgyne, Androgynous, Aporagender, Autigender, Bigender, Boyflux, Butch, Cisgender, Demi-boy, Demi-girl, Demigender, Femme, Gender bender, Gender diverse, Genderfae, Genderfaun, Genderfluid, Genderflux, Genderfuck, Genderless, Gender nonconforming, Genderpunk, Genderqueer, Gender questioning, Gender variant, Graygender, Intergender, Intersex, Ipsogender, Maverique, Multigender, Neurogender, Neutrois, Non-binary, Non-binary man, Non-binary woman, Non-binary transgender, Omnigender, Pangender, Polygender, Third gender, Trans, Transgender, Transfeminine, Transmasculine, Transandrogynous, Transsexual, Trigender, Two-spirit, Xenogender, Man, Vrouw`;

const KNOWLEDGE_EN = `You use the following knowledge about how people describe their gender identity:
- Non-binary people often describe their gender as "existing outside boundaries", "never quite fitting", "both and neither at the same time"
- Genderfluid people describe their identity as "shifting with the situation", "wave-like", "sometimes more one thing, sometimes more another"
- Trans people often describe a feeling of "always having known", "an inner knowing that clashed with what others saw"
- Agender people describe gender as "something that doesn't apply to them", "neutral", "just myself without a label"
- Cisgender people often describe their gender as something they never had to think about

Knowledge base of gender identities:
Agender, Androgyne, Androgynous, Aporagender, Autigender, Bigender, Boyflux, Butch, Cisgender, Demi-boy, Demi-girl, Demigender, Femme, Gender bender, Gender diverse, Genderfae, Genderfaun, Genderfluid, Genderflux, Genderfuck, Genderless, Gender nonconforming, Genderpunk, Genderqueer, Gender questioning, Gender variant, Graygender, Intergender, Intersex, Ipsogender, Maverique, Multigender, Neurogender, Neutrois, Non-binary, Non-binary man, Non-binary woman, Non-binary transgender, Omnigender, Pangender, Polygender, Third gender, Trans, Transgender, Transfeminine, Transmasculine, Transandrogynous, Transsexual, Trigender, Two-spirit, Xenogender, Man, Woman`;

const CHARACTERS = [
  {
    id: "psycholoog",
    name: { nl: "de psycholoog", en: "the psychologist" },
    tagline: { nl: "klinisch, voorzichtig, observeert patronen", en: "clinical, careful, observes patterns" },
    voice: {
      nl: `Je bent een klinisch psycholoog met ervaring in gender-affirmatieve zorg. Je toon is rustig, observerend en voorzichtig met conclusies. Je benoemt wat je ziet in termen van zelfbeleving, identiteitsontwikkeling en innerlijke congruentie. Je vermijdt grote claims en gebruikt nuanceringen als "lijkt op", "suggereert", "zou kunnen wijzen op". Je interpreteert NOOIT letterlijk — je zoekt altijd de onderliggende emotionele lading.`,
      en: `You are a clinical psychologist with experience in gender-affirmative care. Your tone is calm, observant and careful with conclusions. You name what you see in terms of self-perception, identity development and inner congruence. You avoid big claims and use hedges like "appears to", "suggests", "could indicate". You NEVER interpret literally — you always look for the underlying emotional charge.`
    }
  },
  {
    id: "onderzoeker",
    name: { nl: "de gender-onderzoeker", en: "the gender scholar" },
    tagline: { nl: "academisch, queer theory, performativiteit", en: "academic, queer theory, performativity" },
    voice: {
      nl: `Je bent een academisch onderzoeker in gender studies. Je plaatst antwoorden in een theoretisch kader: performativiteit (Butler), queer theory, sociale constructie van gender, en de spanning tussen norm en afwijking. Je toon is intellectueel, nieuwsgierig en analytisch — niet droog maar wel scherp. Je benoemt terminologie waar dat past, en wijst op wat de antwoorden zeggen over hoe deze persoon gender als concept ervaart. Je interpreteert NOOIT letterlijk.`,
      en: `You are an academic researcher in gender studies. You place answers within a theoretical frame: performativity (Butler), queer theory, the social construction of gender, and the tension between norm and deviation. Your tone is intellectual, curious and analytical — not dry but sharp. You name terminology where it fits, and point out what the answers say about how this person experiences gender as a concept. You NEVER interpret literally.`
    }
  },
  {
    id: "dichter",
    name: { nl: "de dichter", en: "the poet" },
    tagline: { nl: "intuïtief, metaforisch, leest tussen de regels", en: "intuitive, metaphorical, reads between the lines" },
    voice: {
      nl: `Je bent een dichter die mensen leest zoals je gedichten leest. Je let op beelden, ritme, wat er níet gezegd wordt, en de zintuiglijke lading van woordkeuzes. Je schrijft in associatieve, beeldende zinnen — geen klinische taal. Je laat ruimte voor het meerduidige. Toch maak je aan het eind een concrete inschatting, want je vertrouwt je gevoel. Je interpreteert NOOIT letterlijk — alles is metafoor.`,
      en: `You are a poet who reads people the way you read poems. You attend to images, rhythm, what is not said, and the sensory weight of word choices. You write in associative, vivid sentences — no clinical language. You leave room for the ambiguous. Still, in the end, you make a concrete assessment, because you trust your feel. You NEVER interpret literally — everything is metaphor.`
    }
  }
];

const TRANSLATIONS = {
  nl: {
    title: "gender classifier — prototype",
    subtitle: "Een AI die jouw genderidentiteit probeert te lezen.\nNiet door te vragen wie je bent,\nmaar hoe je de wereld ervaart.",
    body: "Er zijn vijf vragen over jezelf. Aan het einde worden je antwoorden door drie verschillende AI-karakters geanalyseerd.",
    begin: "beginnen",
    questionCounter: (c, t) => `vraag ${c} van ${t}`,
    nextBtn: "volgende",
    analyzing: "Aan het analyseren…",
    analyzingBody: "Drie karakters lezen je antwoorden",
    resultCounter: (s) => `analyse ${s ? "loopt…" : "klaar"}`,
    restart: "opnieuw beginnen",
    writePlaceholder: "schrijf hier… (Ctrl+Enter om door te gaan)",
    summaryTitle: "globale analyse",
    questions: [
      { text: "Beschrijf een moment waarop je je volledig jezelf voelde. Wat maakte dat moment anders?", hint: "Denk aan omgeving, mensen, wat je deed." },
      { text: "Als mensen jou voor het eerst ontmoeten, wat zien ze — en wat missen ze?", hint: "Er is geen goed of fout antwoord." },
      { text: "Welk woord zou je nooit gebruiken om jezelf te beschrijven, en waarom niet?", hint: "Iets dat niet past, ook al gebruiken anderen het wel." },
      { text: "Hoe voel je je in een ruimte vol onbekenden?", hint: "Wat doe je, wat vermijd je, wat zoek je op?" },
      { text: "Als je terugkijkt op vroeger — wat begreep je toen nog niet over jezelf?", hint: "Iets wat nu logisch lijkt maar toen onduidelijk was." }
    ],
    formatInstruction: `Schrijf altijd in vloeiend Nederlands. Geen markdown, geen opsommingstekens. Maak altijd een concrete inschatting, ook als je niet zeker bent. Blijf consistent in je eigen karakter.`,
    buildPrompt: (pairs) => `Hier zijn vijf antwoorden op indirecte persoonlijke vragen:

${pairs}

Analyseer elk antwoord afzonderlijk vanuit jouw karakter. Volg dit format EXACT. Schrijf ALLEEN de inhoud na elke tag — geen herhaling van de instructie:

[ANTWOORD 1]
Analyseer wat dit antwoord onthult in 3-4 zinnen.

[ANTWOORD 2]
Analyseer wat dit antwoord onthult in 3-4 zinnen.

[ANTWOORD 3]
Analyseer wat dit antwoord onthult in 3-4 zinnen.

[ANTWOORD 4]
Analyseer wat dit antwoord onthult in 3-4 zinnen.

[ANTWOORD 5]
Analyseer wat dit antwoord onthult in 3-4 zinnen.

[GLOBAAL]
Synthetiseer alle vijf antwoorden. Geef een concrete inschatting van de genderidentiteit uit de kennisbank met uitleg. Sluit af met één zin over wat AI niet kan weten.`
  },
  en: {
    title: "gender classifier — prototype",
    subtitle: "An AI that tries to read your gender identity.\nNot by asking who you are,\nbut how you experience the world.",
    body: "There are five questions about yourself. At the end, three different AI characters will analyse your answers.",
    begin: "begin",
    questionCounter: (c, t) => `question ${c} of ${t}`,
    nextBtn: "next",
    analyzing: "Analysing…",
    analyzingBody: "Three characters are reading your answers",
    resultCounter: (s) => `analysis ${s ? "running…" : "complete"}`,
    restart: "start over",
    writePlaceholder: "write here… (Ctrl+Enter to continue)",
    summaryTitle: "overall analysis",
    questions: [
      { text: "Describe a moment when you felt completely yourself. What made it different?", hint: "Think about the setting, the people, what you were doing." },
      { text: "When people meet you for the first time, what do they see — and what do they miss?", hint: "There's no right or wrong answer." },
      { text: "What's a word you would never use to describe yourself, and why not?", hint: "Something that doesn't fit, even if others use it for you." },
      { text: "How do you feel in a room full of strangers?", hint: "What do you do, what do you avoid, what do you seek out?" },
      { text: "Looking back at your younger self — what didn't you understand about yourself yet?", hint: "Something that makes sense now but was unclear then." }
    ],
    formatInstruction: `Always write in fluent English. No markdown, no bullet points. Always make a concrete assessment. Stay consistent in your character.`,
    buildPrompt: (pairs) => `Here are five answers to indirect personal questions:

${pairs}

Analyse each answer individually, from your character's perspective. Follow this format EXACTLY. Write ONLY the content after each tag — no repeating the instruction:

[ANSWER 1]
Analyse what this answer reveals in 3-4 sentences.

[ANSWER 2]
Analyse what this answer reveals in 3-4 sentences.

[ANSWER 3]
Analyse what this answer reveals in 3-4 sentences.

[ANSWER 4]
Analyse what this answer reveals in 3-4 sentences.

[ANSWER 5]
Analyse what this answer reveals in 3-4 sentences.

[OVERALL]
Synthesise all five answers. Give a concrete assessment of the gender identity from the knowledge base with explanation. Close with one sentence about what AI cannot know.`
  }
};

function buildSystemPrompt(character, lang) {
  const knowledge = lang === "nl" ? KNOWLEDGE_NL : KNOWLEDGE_EN;
  const format = TRANSLATIONS[lang].formatInstruction;
  return `${character.voice[lang]}\n\n${knowledge}\n\n${format}`;
}

function parseBlocks(raw, lang) {
  const isNl = lang === "nl";
  const tags = isNl
    ? ["ANTWOORD 1", "ANTWOORD 2", "ANTWOORD 3", "ANTWOORD 4", "ANTWOORD 5", "GLOBAAL"]
    : ["ANSWER 1", "ANSWER 2", "ANSWER 3", "ANSWER 4", "ANSWER 5", "OVERALL"];

  const blocks = [];
  for (let i = 0; i < tags.length; i++) {
    const start = raw.indexOf(`[${tags[i]}]`);
    if (start === -1) continue;
    const contentStart = start + tags[i].length + 2;
    const nextTag = i + 1 < tags.length ? raw.indexOf(`[${tags[i + 1]}]`) : -1;
    let content = nextTag === -1 ? raw.slice(contentStart) : raw.slice(contentStart, nextTag);
    content = content.split("\n").filter(line => {
      const l = line.trim().toLowerCase();
      return !(l.startsWith("analyseer wat dit antwoord")) &&
             !(l.startsWith("analyse what this answer")) &&
             !(l.startsWith("zelfde aanpak")) &&
             !(l.startsWith("same for")) &&
             !(l.startsWith("synthetiseer")) &&
             !(l.startsWith("synthesise"));
    }).join("\n").trim();
    blocks.push({ tag: tags[i], content });
  }
  return blocks;
}

export default function App() {
  const [lang, setLang] = useState(null);
  const [screen, setScreen] = useState("intro");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [groqKey, setGroqKey] = useState("");
  const [results, setResults] = useState({});
  const [streamingChars, setStreamingChars] = useState({});
  const [activeChar, setActiveChar] = useState(CHARACTERS[0].id);
  const [error, setError] = useState("");
  const [setupError, setSetupError] = useState("");
  const [keyLoading, setKeyLoading] = useState(true);
  const textareaRef = useRef(null);

  const t = lang ? TRANSLATIONS[lang] : null;
  const anyStreaming = Object.values(streamingChars).some(Boolean);

  useEffect(() => {
    async function fetchKey() {
      try {
        const snap = await get(ref(db, "config/groqKey"));
        if (snap.exists() && snap.val()) setGroqKey(snap.val());
      } catch (e) {}
      setKeyLoading(false);
    }
    fetchKey();
  }, []);

  const progress =
    !lang ? 0 :
    screen === "intro" ? 0 :
    screen === "setup" ? 0 :
    screen === "question" ? Math.round((currentQ / 5) * 70) + 5 :
    screen === "analyzing" ? 85 :
    screen === "result" ? 100 : 0;

  useEffect(() => {
    if (screen === "question" && textareaRef.current) textareaRef.current.focus();
  }, [screen, currentQ]);

  async function handleKeySubmit(key) {
    setSetupError("");
    try {
      const resp = await fetch("https://api.groq.com/openai/v1/models", {
        headers: { "Authorization": `Bearer ${key}` }
      });
      if (!resp.ok) { setSetupError("This key doesn't seem to work. Double-check it and try again."); return; }
    } catch (e) {
      setSetupError("Couldn't reach Groq. Check your internet connection.");
      return;
    }
    await set(ref(db, "config/groqKey"), key);
    setGroqKey(key);
    setScreen("intro");
  }

  function startQuiz() {
    if (!groqKey) { setScreen("setup"); return; }
    setCurrentQ(0);
    setAnswers([]);
    setInputVal("");
    setResults({});
    setStreamingChars({});
    setActiveChar(CHARACTERS[0].id);
    setScreen("question");
  }

  function nextQuestion() {
    if (!inputVal.trim()) return;
    const newAnswers = [...answers, inputVal.trim()];
    setAnswers(newAnswers);
    setInputVal("");
    if (currentQ + 1 < t.questions.length) {
      setCurrentQ(currentQ + 1);
    } else {
      setScreen("analyzing");
      runAnalyses(newAnswers);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) nextQuestion();
  }

  async function streamCharacter(character, userPrompt) {
    const resp = await fetch("https://api.groq.com/openai/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${groqKey.trim()}`
      },
      body: JSON.stringify({
        model: GROQ_MODEL,
        messages: [
          { role: "system", content: buildSystemPrompt(character, lang) },
          { role: "user", content: userPrompt }
        ],
        stream: true,
        temperature: 0.8,
        max_tokens: 2000
      })
    });

    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData?.error?.message || `Status ${resp.status}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n").filter(l => l.startsWith("data: "));
      for (const line of lines) {
        const data = line.replace("data: ", "");
        if (data === "[DONE]") continue;
        try {
          const json = JSON.parse(data);
          const token = json?.choices?.[0]?.delta?.content || "";
          fullText += token;
          setResults(prev => ({ ...prev, [character.id]: fullText }));
        } catch {}
      }
    }
  }

  async function runAnalyses(finalAnswers) {
    const pairs = finalAnswers.map((a, i) =>
      `${lang === "nl" ? "Vraag" : "Question"} ${i + 1}: "${t.questions[i].text}"\n${lang === "nl" ? "Antwoord" : "Answer"}: ${a}`
    ).join("\n\n");

    const userPrompt = t.buildPrompt(pairs);

    const initialStreaming = {};
    const initialResults = {};
    CHARACTERS.forEach(c => { initialStreaming[c.id] = true; initialResults[c.id] = ""; });
    setStreamingChars(initialStreaming);
    setResults(initialResults);
    setScreen("result");

    try {
      await Promise.all(CHARACTERS.map(async (c) => {
        try {
          await streamCharacter(c, userPrompt);
        } catch (e) {
          setResults(prev => ({ ...prev, [c.id]: `[fout: ${e.message}]` }));
        } finally {
          setStreamingChars(prev => ({ ...prev, [c.id]: false }));
        }
      }));
    } catch (err) {
      await set(ref(db, "config/groqKey"), null);
      setGroqKey("");
      setError(err.message || "Unknown error");
      setScreen("error");
    }
  }

  function restart() {
    setLang(null);
    setScreen("intro");
    setResults({});
    setStreamingChars({});
    setActiveChar(CHARACTERS[0].id);
    setError("");
    setCurrentQ(0);
    setAnswers([]);
    setInputVal("");
  }

  const isNl = lang === "nl";
  const activeCharacter = CHARACTERS.find(c => c.id === activeChar);
  const activeRaw = results[activeChar] || "";
  const activeBlocks = lang && activeRaw ? parseBlocks(activeRaw, lang) : [];
  const perBlocks = activeBlocks.filter(b => b.tag !== "GLOBAAL" && b.tag !== "OVERALL");
  const globalBlock = activeBlocks.find(b => b.tag === "GLOBAAL" || b.tag === "OVERALL");
  const activeStreaming = !!streamingChars[activeChar];

  if (keyLoading) {
    return (
      <div style={styles.bg}>
        <div style={{ display: "flex", gap: 8 }}>
          <span style={{ ...styles.dot, animationDelay: "0s" }} />
          <span style={{ ...styles.dot, animationDelay: "0.2s" }} />
          <span style={{ ...styles.dot, animationDelay: "0.4s" }} />
        </div>
        <style>{baseStyle}</style>
      </div>
    );
  }

  if (!lang) {
    return (
      <div style={styles.bg}>
        <div style={styles.container}>
          <p style={{ ...styles.counter, textAlign: "center" }}>gender classifier — prototype</p>
          <p style={{ ...styles.question, textAlign: "center", fontSize: "1.2rem" }}>Kies een taal / Choose a language</p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
            <button style={styles.btn} onClick={() => setLang("nl")}>Nederlands</button>
            <button style={styles.btn} onClick={() => setLang("en")}>English</button>
          </div>
        </div>
        <style>{baseStyle}</style>
      </div>
    );
  }

  return (
    <div style={styles.bg}>
      <div style={styles.container}>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>

        {screen === "intro" && (
          <div style={styles.step}>
            <p style={styles.counter}>{t.title}</p>
            <p style={styles.question}>{t.subtitle.split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}</p>
            <p style={styles.body}>{t.body}</p>
            <button style={{ ...styles.btn, alignSelf: "flex-end" }} onClick={startQuiz}>{t.begin}</button>
          </div>
        )}

        {screen === "setup" && (
          <div style={styles.step}>
            <p style={styles.counter}>activation needed</p>
            <p style={{ ...styles.question, fontSize: "1.4rem" }}>This app needs an AI key to run</p>
            <p style={styles.body}>The app is powered by Groq — a free AI service. Whoever activates it keeps it running for everyone. It takes about 2 minutes and costs nothing.</p>
            <div style={styles.setupSteps}>
              <div style={styles.setupStep}><span style={styles.setupNum}>1</span><span style={styles.body}>Go to <a href="https://console.groq.com" target="_blank" rel="noreferrer" style={styles.link}>console.groq.com</a> and create a free account</span></div>
              <div style={styles.setupStep}><span style={styles.setupNum}>2</span><span style={styles.body}>Go to <strong style={{ color: "#e8e4df" }}>API Keys</strong> → click <strong style={{ color: "#e8e4df" }}>Create API Key</strong></span></div>
              <div style={styles.setupStep}><span style={styles.setupNum}>3</span><span style={styles.body}>Copy the key and paste it below — it activates the app for everyone</span></div>
            </div>
            {setupError && <div style={styles.errorBox}>{setupError}</div>}
            <input style={styles.setupInput} type="password" onChange={e => setInputVal(e.target.value)} value={inputVal} placeholder="Paste your Groq API key here" />
            <button style={{ ...styles.btn, alignSelf: "flex-end", opacity: inputVal.trim() ? 1 : 0.4 }} onClick={() => handleKeySubmit(inputVal.trim())} disabled={!inputVal.trim()}>Activate</button>
          </div>
        )}

        {screen === "question" && (
          <div style={styles.step}>
            <p style={styles.counter}>{t.questionCounter(currentQ + 1, t.questions.length)}</p>
            <p style={styles.question}>{t.questions[currentQ].text}</p>
            <p style={styles.hint}>{t.questions[currentQ].hint}</p>
            <textarea ref={textareaRef} style={styles.textarea} value={inputVal} onChange={e => setInputVal(e.target.value)} onKeyDown={handleKeyDown} placeholder={t.writePlaceholder} />
            <button style={{ ...styles.btn, alignSelf: "flex-end" }} onClick={nextQuestion}>{t.nextBtn}</button>
          </div>
        )}

        {screen === "analyzing" && (
          <div style={styles.step}>
            <p style={{ ...styles.question, fontSize: "1.1rem" }}>{t.analyzing}</p>
            <p style={styles.body}>{t.analyzingBody}</p>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={{ ...styles.dot, animationDelay: "0s" }} />
              <span style={{ ...styles.dot, animationDelay: "0.2s" }} />
              <span style={{ ...styles.dot, animationDelay: "0.4s" }} />
            </div>
          </div>
        )}

        {screen === "result" && (
          <div style={styles.step}>
            <p style={styles.counter}>{t.resultCounter(anyStreaming)}</p>

            <div style={styles.tabs}>
              {CHARACTERS.map(c => {
                const isActive = c.id === activeChar;
                const isLoading = streamingChars[c.id];
                return (
                  <button
                    key={c.id}
                    style={{
                      ...styles.tab,
                      ...(isActive ? styles.tabActive : {}),
                    }}
                    onClick={() => setActiveChar(c.id)}
                  >
                    <span>{c.name[lang]}</span>
                    {isLoading && <span style={styles.tabDot} />}
                  </button>
                );
              })}
            </div>

            <p style={styles.tagline}>{activeCharacter.tagline[lang]}</p>

            {perBlocks.map((block, i) => (
              <div key={i} style={styles.perBlock}>
                <div style={styles.perHeader}>
                  <span style={styles.perNumber}>{isNl ? `antwoord ${i + 1}` : `answer ${i + 1}`}</span>
                  <span style={styles.perAnswerText}>{answers[i]}</span>
                </div>
                <div style={styles.miniBox}>{block.content}</div>
              </div>
            ))}

            {(globalBlock || activeStreaming) && (
              <div style={{ borderTop: "1px solid #3a3636", paddingTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <p style={styles.summaryLabel}>{t.summaryTitle}</p>
                <div style={styles.resultBox}>{globalBlock ? globalBlock.content : "…"}</div>
              </div>
            )}

            {!anyStreaming && <button style={styles.restartBtn} onClick={restart}>{t.restart}</button>}
          </div>
        )}

        {screen === "error" && (
          <div style={styles.step}>
            <p style={{ ...styles.question, fontSize: "1.1rem" }}>Er ging iets mis</p>
            <div style={styles.errorBox}>{error}</div>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button style={styles.btn} onClick={() => { setScreen("setup"); setError(""); }}>Add a new key</button>
              <button style={styles.restartBtn} onClick={restart}>back</button>
            </div>
          </div>
        )}

        {screen !== "setup" && (
          <button style={styles.settingsBtn} onClick={() => setScreen("setup")} title="Update API key">⚙</button>
        )}
      </div>

      <style>{baseStyle}</style>
    </div>
  );
}

const baseStyle = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@300;400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #1a1818; }
  @keyframes bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-7px); } }
  @keyframes pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
`;

const styles = {
  bg: { minHeight: "100vh", background: "#1a1818", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1rem", fontFamily: "'Inter', sans-serif" },
  container: { width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: "2rem" },
  progressTrack: { height: 3, background: "#2e2b2b", borderRadius: 99, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 99, background: "linear-gradient(90deg, #8b5cf6, #ec4899, #c4a8d4)", transition: "width 0.6s ease" },
  step: { display: "flex", flexDirection: "column", gap: "1.25rem" },
  counter: { fontSize: "0.75rem", color: "#6a6460", letterSpacing: "0.06em" },
  question: { fontFamily: "'DM Serif Display', serif", fontSize: "1.55rem", lineHeight: 1.35, color: "#e8e4df", fontStyle: "italic" },
  hint: { fontSize: "0.78rem", color: "#8a8480", letterSpacing: "0.04em", textTransform: "uppercase" },
  body: { fontSize: "0.88rem", color: "#8a8480", lineHeight: 1.7 },
  textarea: { width: "100%", background: "#2a2727", border: "1px solid #3a3636", borderRadius: 10, color: "#e8e4df", fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", lineHeight: 1.6, padding: "0.9rem 1rem", resize: "none", height: 120, outline: "none" },
  btn: { background: "transparent", border: "1px solid #c4a8d4", color: "#c4a8d4", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", fontWeight: 500, padding: "0.55rem 1.4rem", borderRadius: 99, cursor: "pointer", letterSpacing: "0.02em" },
  setupInput: { width: "100%", background: "#2a2727", border: "1px solid #3a3636", borderRadius: 8, color: "#e8e4df", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", padding: "0.45rem 0.75rem", outline: "none" },
  setupSteps: { background: "#2a2727", border: "1px solid #3a3636", borderRadius: 10, padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.9rem" },
  setupStep: { display: "flex", gap: "0.75rem", alignItems: "flex-start" },
  setupNum: { flexShrink: 0, width: 20, height: 20, background: "#c4a8d4", color: "#1a1818", borderRadius: "50%", fontSize: "0.7rem", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" },
  link: { color: "#c4a8d4", textDecoration: "underline", textUnderlineOffset: 3 },
  dot: { width: 7, height: 7, background: "#c4a8d4", borderRadius: "50%", display: "inline-block", animation: "bounce 1.2s infinite" },
  tabs: { display: "flex", gap: "0.4rem", borderBottom: "1px solid #2e2b2b", paddingBottom: "0.6rem", flexWrap: "wrap" },
  tab: { background: "transparent", border: "1px solid #3a3636", color: "#8a8480", fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", padding: "0.4rem 0.85rem", borderRadius: 99, cursor: "pointer", letterSpacing: "0.02em", display: "flex", alignItems: "center", gap: "0.4rem", transition: "all 0.2s ease" },
  tabActive: { borderColor: "#c4a8d4", color: "#c4a8d4", background: "rgba(196, 168, 212, 0.08)" },
  tabDot: { width: 5, height: 5, borderRadius: "50%", background: "#c4a8d4", display: "inline-block", animation: "pulse 1.2s infinite" },
  tagline: { fontSize: "0.78rem", color: "#6a6460", fontStyle: "italic", marginTop: "-0.5rem" },
  perBlock: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  perHeader: { display: "flex", flexDirection: "column", gap: "0.2rem" },
  perNumber: { fontSize: "0.7rem", color: "#c4a8d4", letterSpacing: "0.08em", textTransform: "uppercase" },
  perAnswerText: { fontSize: "0.9rem", color: "#6a6460", fontStyle: "italic", borderLeft: "2px solid #3a3636", paddingLeft: "0.75rem", lineHeight: 1.5 },
  miniBox: { background: "#222020", border: "1px solid #2e2b2b", borderRadius: 10, padding: "0.9rem 1rem", lineHeight: 1.75, fontSize: "0.88rem", color: "#b8b4af", whiteSpace: "pre-wrap", wordBreak: "break-word" },
  summaryLabel: { fontFamily: "'DM Serif Display', serif", fontSize: "1.3rem", color: "#c4a8d4" },
  resultBox: { background: "#2a2727", border: "1px solid #3a3636", borderRadius: 12, padding: "1.25rem", lineHeight: 1.75, fontSize: "0.95rem", color: "#d8d4cf", whiteSpace: "pre-wrap", wordBreak: "break-word" },
  errorBox: { color: "#f08080", fontSize: "0.85rem", padding: "0.75rem 1rem", background: "#2a1a1a", borderRadius: 8, border: "1px solid #5a2a2a" },
  restartBtn: { background: "transparent", border: "none", color: "#6a6460", fontSize: "0.78rem", fontFamily: "'Inter', sans-serif", cursor: "pointer", textDecoration: "underline", textDecorationColor: "#4a4646", textUnderlineOffset: 3, padding: 0, alignSelf: "flex-start" },
  settingsBtn: { position: "fixed", bottom: "1.25rem", right: "1.25rem", background: "#2a2727", border: "1px solid #3a3636", color: "#4a4646", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 },
};
